import { Component, Inject, OnInit } from '@angular/core';
import { MatDialog, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { isEqual } from 'lodash';
import { SaveScriptDialogComponent } from '../save-script-dialog/save-script-dialog.component';
import { PrecheckScriptDialogComponent } from '../precheck-script-dialog/precheck-script-dialog.component';
import {
  ValuesExchangeProvider,
  NotificationService,
  DialogService,
  TemporaryScriptSuffix
} from '@nrwl/common-lib';
import { SCRIPT_TEMPLATE } from './script-template';

@Component({
  selector: 'nrwl-add-script-popup',
  templateUrl: './add-script-popup.component.html',
  styleUrls: ['./add-script-popup.component.scss']
})
export class AddScriptPopupComponent implements OnInit {
  model: string;
  editorValue: string;
  scriptName: string = '';
  selectedAgent = [];
  constructor(
    public dialog: MatDialog,
    private dialogRef: MatDialogRef<AddScriptPopupComponent>,
    private exchange: ValuesExchangeProvider,
    private notificationService: NotificationService,
    private dialogService: DialogService,
    // eslint-disable-next-line
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}
  ngOnInit(): void {
    this.exchange.registerInterest('updated-code-editor', data => {
      this.editorValue = data;
    });
    this.model = SCRIPT_TEMPLATE;

    if (this.data.editMode) {
      this.model = this.data.scriptDetails;
      this.scriptName = this.data.isPreCheckPassed
        ? this.data.scriptName + TemporaryScriptSuffix
        : this.data.scriptName;
    }
  }
  // eslint-disable-next-line
  openScriptDialog(value?: any, callback?: Function): void {
    const popupObject = {
      editorValue: this.editorValue,
      id: this.data.id,
      editMode: this.data.editMode,
      scriptName: this.scriptName,
      showConfirmationPopup: value?.showConfirmationPopup,
      allScriptNames: this.data?.allScriptNames,
      precheckOperation: value?.precheckOperation,
      preCheckedPassed: this.data.isPreCheckPassed
    };
    const dialogRef = this.dialog.open(SaveScriptDialogComponent, {
      width: '500px',
      disableClose: true,
      data: popupObject
    });
    dialogRef.afterClosed().subscribe(data => {
      if (data.precheckOperation) {
        this.scriptName = data.scriptName;
        if (typeof callback === 'function') {
          callback();
        } else {
          this.dialogRef.close();
        }
      } else if (data.dialogClose) {
        this.scriptName = data.scriptName;
        this.dialogRef.close();
      }
    });
  }

  copyEditorValue(): void {
    this.notificationService.onSuccess('Script copied to clipboard');
  }

  onCloseIcon(): void {
    if (this.editorValue && !isEqual(this.model, this.editorValue)) {
      this.openScriptDialog({ showConfirmationPopup: true });
    } else {
      this.dialogRef.close();
    }
  }

  isEmptyScript(): boolean {
    return !this.editorValue?.trim();
  }

  detectURLs(editorValue: string): Array<string> {
    const urlRegex = /((?<=goto)(.*)(?=\))[^\s]+)/;
    return editorValue.match(urlRegex);
  }

  validateScriptURL(): { valid: boolean; errorMsg: string } {
    let response = { valid: true, errorMsg: '' };
    const hostName = new URL(this.data.url).hostname;
    const domainValues = hostName.split('.');
    const domainName = domainValues.length > 2 ? hostName.replace(domainValues[0], '') : hostName;
    const availableURLs = this.detectURLs(this.editorValue);
    if (availableURLs?.length) {
      availableURLs.forEach(url => {
        if (response.valid && !url.includes(domainName)) {
          const lineNo = this.editorValue
            .substring(0, this.editorValue.indexOf(url))
            .split('\n').length;
          response = {
            valid: false,
            errorMsg: `URL ${url} at line number ${lineNo} does not match the application's URL ${this.data.url}`
          };
        }
      });
    }

    return response;
  }

  SaveOrPrecheckScript(): void {
    if (this.data.url && !this.validateScriptURL().valid) {
      this.dialogService.showInfo(
        'information',
        'body',
        this.validateScriptURL().errorMsg,
        'Precheck error'
      );
      return;
    }
    if ((this.editorValue.match(/^.*[a-zA-Z0-9]+.*VIAVISTEP/gm) || []).length) {
      this.dialogService.showInfo(
        'information',
        'body',
        'VIAVISTEP step definition annotations must be on their own line.',
        'Precheck error'
      );
      return;
    }
    if ((this.editorValue.match(/VIAVISTEP/g) || []).length > 15) {
      this.dialogService.showInfo(
        'information',
        'body',
        'Number of viavi steps should be less than or equal to 15.',
        'Precheck error'
      );
      return;
    }
    if (this.editorValue.split('\n').length > 200) {
      this.dialogService.showInfo(
        'information',
        'body',
        'Number of script lines should be less than or equal to 200.',
        'Precheck error'
      );
      return;
    }

    if (!this.scriptName) {
      // script not available create one
      this.openScriptDialog('', this.openPrecheckScriptDialog.bind(this));
    } else {
      //updating the script - call precheck operation
      this.openScriptDialog({ precheckOperation: true }, this.openPrecheckScriptDialog.bind(this));
    }
  }

  openPrecheckScriptDialog(): void {
    this.model = this.editorValue;
    const popupObject = {
      appId: this.data.id,
      editMode: this.data.editMode,
      scriptName: this.scriptName,
      selectedAgent: this.selectedAgent
    };
    const dialogRef = this.dialog.open(PrecheckScriptDialogComponent, {
      width: '500px',
      disableClose: true,
      data: popupObject
    });
    dialogRef.afterClosed().subscribe(data => {
      this.exchange.notifyListeners('create_script', false);
      if (data.dialogClose) {
        this.dialogRef.close();
      }
      this.selectedAgent = data.selectedAgent;
      this.exchange.notifyListeners('create_script', true);
    });
  }
}
